'use strict';

import React, {Component} from 'react';

import DocumentTitle from '@uw-it-sis/lib-react/lib/DocumentTitle';
import {observer} from "mobx-react";
import {LinkContainer} from "react-router-bootstrap";

import {FaCaretLeft, FaCaretRight, FaSyncAlt} from "react-icons/fa";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";

import Lists from '@uw-it-sis/lib-react/lib/Lists';
import SimpleState from '@uw-it-sis/lib-react/lib/SimpleState';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {withRouter} from "@uw-it-sis/lib-react/lib/WithRouter";

import RelatedLinks from "@uw-it-sis/lib-react-myplan/lib/RelatedLinks";

import scheduleBuilderStore from './ScheduleBuilderStore';
import ScheduleBuilderDetails from './ScheduleBuilderDetails';
import ScheduleBuilderOnBoarding from "./ScheduleBuilderOnBoarding";
import ScheduleBuilderDetailsStore from "./ScheduleBuilderDetailsStore";
import IntendedGraduation from "../common/IntendedGraduation";

class ScheduleBuilder extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = scheduleBuilderStore;

        this.buildDoneComponent = this.buildDoneComponent.bind(this);
    }

    componentDidMount() {
        this.store.mount();
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    toggleOnBoarding = () => {
        this.store.toggleOnboardingFlag();
    }

    /**
     * Click handler for data refresh button.
     */
    handleRefreshDataClick() {
        ScheduleBuilderDetailsStore.refreshData();
    }

    buildDoneComponent() {
        let component;
        let termCalculateResult = this.store.calculateDisplayTerm(this.props.params.term);

        // Show error messaging if there are any
        if ( termCalculateResult.errorMsgs.length > 0 ) {
            component =
                <Alert variant="danger">
                    <Lists data={termCalculateResult.errorMsgs} unstyled />
                </Alert>;
        } else {
            let termName = TermUtils.makeTermDescriptionFromTermId(termCalculateResult.termId);


            // Create a disabled nav button if navigating to previous term isn't allowed
            let prevTermNav = <Button variant="navigate" aria-label={`No previous quarter available`} className="px-0 py-1" disabled><FaCaretLeft aria-hidden="true" size="1.75rem"  /></Button>;
            // Create a disabled nav button if navigating to next term isn't allowed
            let nextTermNav = <Button variant="navigate" aria-label={`No next quarter year available`} className="px-0 py-1" disabled><FaCaretRight aria-hidden="true" size="1.75rem"  /></Button>;

            let navigation = this.store.getNavigation(termCalculateResult.termId);

            // If a previous term is provided, set prev button to navigate to that term
            if ( navigation.prev ) {
                let prevTitle = `Previous quarter: ${TermUtils.makeTermDescriptionFromTermId(navigation.prev)}`;
                prevTermNav =
                    <LinkContainer to={`/schedule/${TermUtils.makeTermLinkIdentifierFromTermId(navigation.prev)}`}>
                        <Button variant="navigate" title={prevTitle} aria-label={prevTitle} className="px-0 py-1"><FaCaretLeft aria-hidden="true" size="1.75rem"  /></Button>
                    </LinkContainer>;
            }

            // If a future term is provided, set next button to navigate to that term
            if ( navigation.next ) {
                let nextTitle = `Next quarter: ${TermUtils.makeTermDescriptionFromTermId(navigation.next)}`
                nextTermNav =
                    <LinkContainer to={`/schedule/${TermUtils.makeTermLinkIdentifierFromTermId(navigation.next)}`}>
                        <Button variant="navigate" title={nextTitle} aria-label={nextTitle}
                                className="px-0 py-1"><FaCaretRight aria-hidden="true" size="1.75rem" /></Button>
                    </LinkContainer>;
            }

            let onBoarding = <ScheduleBuilderOnBoarding show={!this.store.onboardingFlag} hideModal={this.toggleOnBoarding} />

            component =
                <>
                    <div className="d-flex align-items-start justify-content-between">
                        <div>
                            <h2 className="mb-0">{termName}</h2>
                            <IntendedGraduation termId={termCalculateResult.termId} showText />
                        </div>
                        <ButtonGroup aria-label="Academic Year Navigation" className="ms-3">
                            {prevTermNav}
                            {nextTermNav}
                        </ButtonGroup>
                    </div>
                    <Row className="mt-3 d-flex align-items-end">
                        <Col xs={{span: 12, order: 1}} lg={{span: 3, order: 2}} className="text-end">
                            <Button variant="inline-link"
                                    size="sm"
                                    onClick={this.handleRefreshDataClick}>
                                <FaSyncAlt className="me-2" aria-hidden="true"/>
                                Refresh all
                            </Button>
                        </Col>
                        <Col xs={{span: 12, order: 2}} lg={{span: 9, order: 1}} className="mt-3 mt-lg-0">
                            Schedule Builder has generated schedule options from your planned courses and/or sections. Compare options,
                            view schedule details, and select a schedule to add to your plan. <Button
                            variant="inline-link" onClick={this.toggleOnBoarding}>Check out Schedule Builder at a glance</Button> for quick tips.
                            If you have questions, <a href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/schedule-builder/" target="_blank"> visit MyPlan help</a> for more info.
                            {onBoarding}
                        </Col>
                    </Row>
                    <ScheduleBuilderDetails termId={termCalculateResult.termId} />
                </>;
        }

        return component;
    }

    render() {
        let relatedLinkList = [
            {
                label: "Course Search",
                href: "/course",
                router: false,
            },
            {
                label: "Academic Year Plan",
                href: "/",
                router: true,
            },
            {
                label: "Plan Audit",
                href: "/audit/#/plan",
                router: false,
            }
        ];
        if ( appStore.isStudent() || appStore.isAdviser() ) {
            relatedLinkList.splice(2, 0, {
                label: "Degree Audit (DARS)",
                href: "/audit/#/degree",
                router: false,
            })
        }

        const relatedLinks = <RelatedLinks links={relatedLinkList} />;

        return (
            <DocumentTitle title="Schedule Builder - MyPlan">
                <>
                    <div className="page-heading">
                        <h1>Schedule Builder</h1>
                    </div>
                    <SimpleState state={this.store.state}
                                 doneComponentBuilder={this.buildDoneComponent}
                                 pendingComponentBuilder="dots"
                                 name="ScheduleBuilder"/>
                    {relatedLinks}
                </>
            </DocumentTitle>
        );
    }
}

export default withRouter(observer(ScheduleBuilder));